/**
 * Ticket status helpers aligned with admin-be:
 *   state: open | need_assign | failed | closed
 *   assigned_to.source: ai_escalated | human_takeover | system_escalated
 */

export const TICKET_STATE = {
  OPEN: "open",
  NEED_ASSIGN: "need_assign",
  FAILED: "failed",
  CLOSED: "closed",
} as const;

/** Inbox filter / badge buckets (completed = closed). */
export const TICKET_DISPLAY_STATUS = {
  ALL: "all",
  OPEN: "open",
  NEED_ASSIGN: "need_assign",
  /** @deprecated Prefer NEED_ASSIGN — kept for URLs and old copy. */
  ESCALATED: "need_assign",
  FAILED: "failed",
  COMPLETED: "completed",
} as const;

export type TicketDisplayStatus =
  | typeof TICKET_DISPLAY_STATUS.OPEN
  | typeof TICKET_DISPLAY_STATUS.NEED_ASSIGN
  | typeof TICKET_DISPLAY_STATUS.FAILED
  | typeof TICKET_DISPLAY_STATUS.COMPLETED;

export type AssignedToValue =
  | string
  | {
      role?: string | null;
      id?: string | null;
      name?: string | null;
      source?: string | null;
    }
  | null
  | undefined;

export type TicketStatusFields = {
  status?: string | null;
  state?: string | null;
  awaiting?: string | null;
  handling_mode?: string | null;
  /** @deprecated */
  internal_status?: string | null;
  assigned_to?: AssignedToValue;
  /** @deprecated */
  escalated_at?: string | null;
  resolution?: string | null;
};

export const asStatusToken = (value?: string | null) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const normalizeAssigned = (value: AssignedToValue) => {
  if (!value) return null;
  if (typeof value === "string") {
    const id = value.trim();
    return id ? { role: "user" as const, id } : null;
  }
  const role = String(value.role || "").trim().toLowerCase() || null;
  const id = value.id != null ? String(value.id).trim() : "";
  const source = value.source != null ? String(value.source).trim() : null;
  return {
    role,
    id: id || null,
    source,
  };
};

/** Unassigned human queue (need_assign). */
export const isUnassignedHuman = (row: TicketStatusFields): boolean => {
  const state = asStatusToken(row.state);
  if (state === TICKET_STATE.NEED_ASSIGN || state === "need_reply") return true;
  const assigned = normalizeAssigned(row.assigned_to);
  if (assigned?.role === "user" && !assigned.id) return true;
  const source = asStatusToken(assigned?.source);
  if (
    !assigned?.id &&
    (source === "ai_escalated" ||
      source === "system_escalated" ||
      source === "human_takeover")
  ) {
    return true;
  }
  return false;
};

/**
 * Map a ticket / conversation row onto an inbox display bucket.
 * Prefer `state`; fall back to legacy status / internal_status.
 */
export const displayStatusOf = (row: TicketStatusFields): TicketDisplayStatus => {
  const state = asStatusToken(row.state);
  if (state === TICKET_STATE.FAILED) return TICKET_DISPLAY_STATUS.FAILED;
  if (state === TICKET_STATE.CLOSED) {
    return asStatusToken(row.resolution) === "failed"
      ? TICKET_DISPLAY_STATUS.FAILED
      : TICKET_DISPLAY_STATUS.COMPLETED;
  }
  if (state === TICKET_STATE.NEED_ASSIGN || state === "need_reply") {
    return TICKET_DISPLAY_STATUS.NEED_ASSIGN;
  }
  if (state === TICKET_STATE.OPEN) {
    // Claimed by a human stays open; only the unassigned queue is need_assign.
    if (isUnassignedHuman(row)) return TICKET_DISPLAY_STATUS.NEED_ASSIGN;
    return TICKET_DISPLAY_STATUS.OPEN;
  }

  // Legacy internal_status (pre-migration documents).
  const internal = asStatusToken(row.internal_status);
  if (internal === "failed") return TICKET_DISPLAY_STATUS.FAILED;
  if (internal === "handled_by_ai" || internal === "closed") {
    return TICKET_DISPLAY_STATUS.COMPLETED;
  }
  if (
    internal === "ai_escalated" ||
    internal === "human_takeover" ||
    internal === "system_escalated"
  ) {
    return TICKET_DISPLAY_STATUS.NEED_ASSIGN;
  }
  if (internal === "handling_by_ai") return TICKET_DISPLAY_STATUS.OPEN;

  const status = asStatusToken(row.status);
  if (status === TICKET_DISPLAY_STATUS.FAILED || status === "failed") {
    return TICKET_DISPLAY_STATUS.FAILED;
  }
  if (
    status === TICKET_STATE.NEED_ASSIGN ||
    status === "need_reply" ||
    status === "needs_reply" ||
    status === "escalated" ||
    status === "assigned" ||
    status === "need_review" ||
    status.includes("need_review")
  ) {
    return TICKET_DISPLAY_STATUS.NEED_ASSIGN;
  }
  if (
    status === TICKET_DISPLAY_STATUS.COMPLETED ||
    status === "closed" ||
    status === "resolved" ||
    status === "handled_by_ai"
  ) {
    return TICKET_DISPLAY_STATUS.COMPLETED;
  }
  if (status === TICKET_DISPLAY_STATUS.OPEN) return TICKET_DISPLAY_STATUS.OPEN;

  if (isUnassignedHuman(row) || Boolean(row.escalated_at)) {
    return TICKET_DISPLAY_STATUS.NEED_ASSIGN;
  }

  return TICKET_DISPLAY_STATUS.OPEN;
};

/** Normalize a filter / URL status token onto a display bucket (or all). */
export const parseInboxStatus = (value?: string | null): string => {
  const token = asStatusToken(value);
  if (!token || token === TICKET_DISPLAY_STATUS.ALL) {
    return TICKET_DISPLAY_STATUS.ALL;
  }
  if (token === "handling_by_ai") return TICKET_DISPLAY_STATUS.OPEN;
  if (
    token === TICKET_STATE.NEED_ASSIGN ||
    token === "need_reply" ||
    token === "needs_reply" ||
    token === "escalated" ||
    token === "ai_escalated" ||
    token === "system_escalated" ||
    token === "human_takeover" ||
    token === "need_review"
  ) {
    return TICKET_DISPLAY_STATUS.NEED_ASSIGN;
  }
  if (token === "handled_by_ai" || token === "closed" || token === "resolved") {
    return TICKET_DISPLAY_STATUS.COMPLETED;
  }
  if (
    token === TICKET_DISPLAY_STATUS.OPEN ||
    token === TICKET_DISPLAY_STATUS.FAILED ||
    token === TICKET_DISPLAY_STATUS.COMPLETED
  ) {
    return token;
  }
  return TICKET_DISPLAY_STATUS.ALL;
};

export const isNeedsAssign = (row: TicketStatusFields): boolean => {
  if (asStatusToken(row.state) === TICKET_STATE.CLOSED) return false;
  const display = displayStatusOf(row);
  return display === TICKET_DISPLAY_STATUS.NEED_ASSIGN;
};

/** @deprecated Use isNeedsAssign */
export const isNeedsReply = isNeedsAssign;

export const isFailedTicket = (row: TicketStatusFields): boolean =>
  displayStatusOf(row) === TICKET_DISPLAY_STATUS.FAILED;

export const isClosedTicket = (row: TicketStatusFields): boolean => {
  const display = displayStatusOf(row);
  return (
    display === TICKET_DISPLAY_STATUS.COMPLETED ||
    display === TICKET_DISPLAY_STATUS.FAILED
  );
};

export const isAiHandling = (row: TicketStatusFields): boolean => {
  if (asStatusToken(row.state) === TICKET_STATE.CLOSED) return false;
  const display = displayStatusOf(row);
  if (
    display === TICKET_DISPLAY_STATUS.FAILED ||
    display === TICKET_DISPLAY_STATUS.COMPLETED ||
    display === TICKET_DISPLAY_STATUS.NEED_ASSIGN
  ) {
    return false;
  }
  const assigned = normalizeAssigned(row.assigned_to);
  if (assigned?.role === "user") return false;
  if (asStatusToken(row.handling_mode) === "human") return false;
  return true;
};

/** Inbox “Handled by” filter buckets. */
export const HANDLED_BY_FILTER = {
  ALL: "all",
  AI: "ai",
  TEAM: "team",
  NONE: "none",
} as const;

export type HandledByFilter =
  (typeof HANDLED_BY_FILTER)[keyof typeof HANDLED_BY_FILTER];

export const parseHandledByFilter = (value?: string | null): HandledByFilter => {
  const token = asStatusToken(value);
  if (
    token === HANDLED_BY_FILTER.AI ||
    token === HANDLED_BY_FILTER.TEAM ||
    token === HANDLED_BY_FILTER.NONE
  ) {
    return token;
  }
  return HANDLED_BY_FILTER.ALL;
};

/** Who currently owns the live ticket for inbox filtering. */
export const handledByBucket = (
  row: TicketStatusFields
): typeof HANDLED_BY_FILTER.AI | typeof HANDLED_BY_FILTER.TEAM => {
  if (isNeedsAssign(row)) return HANDLED_BY_FILTER.TEAM;
  const assigned = normalizeAssigned(row.assigned_to);
  if (assigned?.role === "user") return HANDLED_BY_FILTER.TEAM;
  if (asStatusToken(row.handling_mode) === "human") {
    return HANDLED_BY_FILTER.TEAM;
  }
  return HANDLED_BY_FILTER.AI;
};

export const matchesHandledByFilter = (
  row: TicketStatusFields,
  filter: HandledByFilter
): boolean => {
  if (filter === HANDLED_BY_FILTER.ALL) return true;
  if (filter === HANDLED_BY_FILTER.NONE) return false;
  if (filter === HANDLED_BY_FILTER.AI) {
    const mode = asStatusToken(row.handling_mode);
    return mode === "ai_autonomous" || mode === "ai";
  }
  return handledByBucket(row) === filter;
};

/** Map the single “Handled by AI only” checkbox to a filter value. */
export const handledByFromAiOnly = (aiOnly: boolean): HandledByFilter =>
  aiOnly ? HANDLED_BY_FILTER.AI : HANDLED_BY_FILTER.ALL;

export const handledByFromChecks = (
  includeAi: boolean,
  includeTeam: boolean
): HandledByFilter => {
  if (includeAi && includeTeam) return HANDLED_BY_FILTER.ALL;
  if (includeAi) return HANDLED_BY_FILTER.AI;
  if (includeTeam) return HANDLED_BY_FILTER.TEAM;
  return HANDLED_BY_FILTER.NONE;
};

/** Optimistic local patch after a status change from the UI. */
export const statusFieldsForDisplay = (newStatus: string) => {
  const token = parseInboxStatus(newStatus);
  if (token === TICKET_DISPLAY_STATUS.OPEN) {
    return {
      status: TICKET_DISPLAY_STATUS.OPEN,
      state: TICKET_STATE.OPEN,
      handling_mode: "ai_autonomous",
      awaiting: "customer" as const,
      resolution: null,
    };
  }
  if (token === TICKET_DISPLAY_STATUS.NEED_ASSIGN) {
    return {
      status: TICKET_DISPLAY_STATUS.NEED_ASSIGN,
      state: TICKET_STATE.NEED_ASSIGN,
      handling_mode: "human",
      awaiting: "us" as const,
      assigned_to: {
        role: "user",
        source: "system_escalated",
      },
      resolution: null,
    };
  }
  if (token === TICKET_DISPLAY_STATUS.FAILED) {
    return {
      status: TICKET_DISPLAY_STATUS.FAILED,
      state: TICKET_STATE.FAILED,
      resolution: "failed",
    };
  }
  return {
    status: TICKET_DISPLAY_STATUS.COMPLETED,
    state: TICKET_STATE.CLOSED,
  };
};

/** Reports KPI bucket — "escalated" means the need_assign queue. */
export const getConversationDisplayStatus = (
  row: TicketStatusFields
): "open" | "escalated" | "failed" | "completed" => {
  const display = displayStatusOf(row);
  if (display === TICKET_DISPLAY_STATUS.NEED_ASSIGN) return "escalated";
  if (display === TICKET_DISPLAY_STATUS.FAILED) return "failed";
  if (display === TICKET_DISPLAY_STATUS.COMPLETED) return "completed";
  return "open";
};
